/**
 * The docked AI side panel (SellerMate W6.3). Mounted once in the app shell;
 * opened from the header's "Ask AI" button or from a grid's own button, which
 * passes the grid's context — page, date range, selected rows — as chips.
 *
 * Three tabs:
 *   Ask       — a short chat with the read-only Analytics Agent
 *               (POST /api/chat/analytics). The context chips ride along as
 *               a preamble; removing a chip drops it from the next question.
 *               The page's date range rides with the question when one is
 *               attached (chatOptions); the empty state says which windows
 *               apply (aiDataWindows).
 *   Prompts   — the prompt library; "Use" fills the composer.
 *   Briefings — delivered answers of scheduled prompts + the schedules.
 *
 * Nothing in the panel changes ads data: the agent only reads.
 */
import { useEffect, useRef, useState } from 'react'
import { Loader2, Send, Sparkles, X } from 'lucide-react'
import { Button, IconButton, Tabs } from '../ui'
import { api } from '../../lib/api'
import { useApiQuery } from '../../lib/query/useApiQuery'
import { aiApi, aiKeys, type LibraryPrompt } from '../../lib/aiApi'
import {
  aiDataWindows, chatOptions, closeAiPanel, composeQuestion, contextChips, setAiContext, setAiDraft,
  setAiTab, useAiPanel, withoutChip, type AiTab,
} from '../../lib/aiPanel'
import PromptLibrary from './PromptLibrary'
import ScheduledBriefings, { type ScheduleDraft } from './ScheduledBriefings'
import { cn } from '../../lib/cn'

type Msg = { role: 'user' | 'assistant'; content: string; error?: boolean }

export default function AiSidePanel() {
  const panel = useAiPanel()
  const [history, setHistory] = useState<Msg[]>([])
  const [busy, setBusy] = useState(false)
  const [scheduleDraft, setScheduleDraft] = useState<ScheduleDraft | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const briefings = useApiQuery(aiKeys.briefings(), aiApi.briefings, {
    poll: 5 * 60_000, enabled: panel.open,
  })

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ behavior: 'smooth' })
  }, [history])

  useEffect(() => {
    if (panel.open && panel.tab === 'ask') composerRef.current?.focus()
  }, [panel.open, panel.tab, panel.draft])

  if (!panel.open) return null

  const chips = contextChips(panel.context)
  const unread = briefings.data?.unread ?? 0

  async function send() {
    const q = panel.draft.trim()
    if (!q || busy) return
    const question = composeQuestion(q, panel.context)
    const prior = history.filter((m) => !m.error).map(({ role, content }) => ({ role, content }))
    setAiDraft('')
    setHistory((h) => [...h, { role: 'user', content: q }])
    setBusy(true)
    try {
      // The chips ARE the request: a removed range chip is not sent (PR 12b, C10).
      const r = await api.chatAnalytics(question, prior, chatOptions(panel.context))
      setHistory((h) => [...h, { role: 'assistant', content: r.answer }])
    } catch (e) {
      setHistory((h) => [...h, { role: 'assistant', content: `The assistant could not answer: ${String(e)}`, error: true }])
    } finally {
      setBusy(false)
    }
  }

  function applyPrompt(p: LibraryPrompt) {
    setAiDraft(p.prompt)
    setAiTab('ask')
  }

  function schedulePrompt(p: LibraryPrompt) {
    setScheduleDraft({ name: p.title, prompt: p.prompt })
    setAiTab('briefings')
  }

  return (
    <aside
      aria-label="AI assistant"
      className="fixed right-0 top-12 bottom-0 z-40 w-full sm:w-[420px] bg-surface border-l border-edge shadow-xl flex flex-col"
    >
      <div className="flex items-center justify-between px-4 h-11 border-b border-edge shrink-0">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink">
          <Sparkles size={15} className="text-accent" aria-hidden /> AI assistant
        </div>
        <IconButton aria-label="Close AI assistant" onClick={closeAiPanel}><X size={15} /></IconButton>
      </div>

      <Tabs<AiTab>
        className="px-3 shrink-0"
        items={[
          { key: 'ask', label: 'Ask' },
          { key: 'prompts', label: 'Prompts' },
          { key: 'briefings', label: 'Briefings', count: unread > 0 ? unread : undefined },
        ]}
        active={panel.tab}
        onChange={setAiTab}
      />

      {panel.tab === 'ask' && (
        <>
          {chips.length > 0 && (
            <div className="flex flex-wrap gap-1.5 px-4 pt-3 shrink-0" aria-label="Context sent with your question">
              {chips.map((c) => (
                <span key={c.key} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs bg-accent-soft text-accent-soft-fg">
                  {c.label}
                  <button
                    type="button"
                    onClick={() => setAiContext(withoutChip(panel.context, c.key))}
                    aria-label={`Remove ${c.label}`}
                    className="rounded-full p-0.5 hover:bg-accent-soft-2"
                  >
                    <X size={11} aria-hidden />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {history.length === 0 && (
              <div className="text-sm text-ink-muted text-center py-6">
                Ask about your ads in plain English, or pick a question from{' '}
                <button type="button" className="text-accent underline" onClick={() => setAiTab('prompts')}>
                  the prompt library
                </button>.
                <div className="mt-2 text-xs">{aiDataWindows(panel.context)}</div>
                {chips.length > 0 && <div className="mt-2 text-xs">The chips above are sent with your question.</div>}
              </div>
            )}
            {history.map((m, i) => (
              <div key={i} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                <div className={cn(
                  'max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap',
                  m.role === 'user' ? 'bg-accent text-accent-fg'
                    : m.error ? 'bg-danger-soft text-danger-soft-fg' : 'bg-surface-2 text-ink',
                )}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex items-center gap-2 text-xs text-ink-muted">
                <Loader2 size={13} className="animate-spin" aria-hidden /> Thinking…
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <div className="border-t border-edge p-3 shrink-0">
            <textarea
              ref={composerRef}
              value={panel.draft}
              onChange={(e) => setAiDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send() }
              }}
              rows={3}
              placeholder="Ask a question… (Enter to send, Shift+Enter for a new line)"
              aria-label="Your question"
              disabled={busy}
              className="w-full rounded-lg border border-edge-strong bg-surface text-ink text-sm px-3 py-2 placeholder:text-ink-faint focus:outline-none focus:border-accent resize-none"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-ink-faint">Read-only: the assistant cannot change anything.</span>
              <Button size="sm" variant="primary" onClick={() => void send()} disabled={busy || !panel.draft.trim()}>
                {busy ? <Loader2 size={13} className="animate-spin" aria-hidden /> : <Send size={13} aria-hidden />}
                Send
              </Button>
            </div>
          </div>
        </>
      )}

      {panel.tab === 'prompts' && (
        <div className="flex-1 overflow-y-auto px-4 py-3">
          <PromptLibrary onUse={applyPrompt} onSchedule={schedulePrompt} />
        </div>
      )}

      {panel.tab === 'briefings' && (
        <div className="flex-1 overflow-y-auto px-4 py-3">
          <ScheduledBriefings draft={scheduleDraft} onDraftConsumed={() => setScheduleDraft(null)} />
        </div>
      )}
    </aside>
  )
}
